import React, { useCallback, useRef, useState } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { AgGridOverlay } from "@src/components/overlays/ag_grid_overlay";
import { CreateDepartmentModal } from "@src/features/employee_management/components/create_department_modal";
import {
  getColumnDefs,
  getGridOptions,
} from "@src/features/employee_management/utils/department_table_grid_options";
import { useGetDepartmentsQuery } from "@src/services/slices/departmentsSlice";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Button, Flex } from "antd";
import _ from "lodash";
import "./department_data_table.less";

const DepartmentDataTable: React.FC = () => {
  const gridApi = useRef<GridApi>(null);

  /* use states */
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [showCreateDepartmentModal, setShowCreateDepartmentModal] =
    useState<boolean>(false);

  /* rtk queries and mutations */
  const { data: departmentsData, isFetching } = useGetDepartmentsQuery();

  /* -------- AG Grid functions --------  */
  /**
   * AG Grid onGridReady function
   * assigns the GridApi to a ref and resizes the columns so that the table is displayed correctly
   * also enables the filter bar and add button when table is ready
   *
   * @param {GridReadyEvent} event AG Grid event
   */
  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApi.current = event.api;
    event.api.sizeColumnsToFit();
    setIsTableLoading(false);
  }, []);

  function handleModalVisibilityChange(visible: boolean) {
    setShowCreateDepartmentModal(visible);
  }

  return (
    <>
      <CreateDepartmentModal
        modalVisible={showCreateDepartmentModal}
        setModalVisible={handleModalVisibilityChange}
      />
      <div data-testid="departments-table" className="department-table">
        <Flex
          className="department-table__top-section"
          data-testid="top-section"
          justify="flex-end"
        >
          <Button
            onClick={() => setShowCreateDepartmentModal(true)}
            size="large"
            type="primary"
            icon={<PlusOutlined />}
            disabled={isFetching || isTableLoading}
            data-testid="add-button"
          >
            Add Department
          </Button>
        </Flex>
        <AgGridReact
          rowData={_.cloneDeep(departmentsData)}
          gridOptions={getGridOptions()}
          noRowsOverlayComponent={AgGridOverlay}
          noRowsOverlayComponentParams={{
            text: "No departments found",
            customStyle: { marginTop: "48px" },
          }}
          loadingOverlayComponent={AgGridOverlay}
          loadingOverlayComponentParams={{
            text: "Loading departments...",
            customStyle: { marginTop: "48px" },
          }}
          onGridReady={onGridReady}
          columnDefs={getColumnDefs()}
        />
      </div>
    </>
  );
};

export default DepartmentDataTable;
